/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/templates/qaTemplateProductDraft.ts
 * FILE_ID: QA-TEMPLATES-PRODUCT-DRAFT-001
 * PURPOSE: Names added in the Templates window, and the job rows they write after insert.
 */
import { namesToEdit } from '../../../qa/qaProductResolve'
import type { QaTemplatePlacement } from '../../../qa/qaTemplateTypes'

export type QaTemplateProductFocus =
  | { kind: 'location'; locationType: string }
  | { kind: 'checklist'; sectionId: string }
  | { kind: 'step'; sectionId: string; itemId: string }

export interface QaTemplateProductDraft {
  locationNames: Record<string, string[]>
  sectionNames: Record<string, string[]>
  itemNames: Record<string, string[]>
}

export const EMPTY_PRODUCT_DRAFT: QaTemplateProductDraft = {
  locationNames: {},
  sectionNames: {},
  itemNames: {},
}

export function namesForFocus(
  draft: QaTemplateProductDraft,
  focus: QaTemplateProductFocus,
  locationType: string | null,
): string[] {
  const location = locationType ? draft.locationNames[locationType] ?? [] : []
  if (focus.kind === 'location') return [...(draft.locationNames[focus.locationType] ?? [])]
  if (focus.kind === 'checklist') return namesToEdit(draft.sectionNames[focus.sectionId], location)
  const section = draft.sectionNames[focus.sectionId] ?? []
  return namesToEdit(draft.itemNames[focus.itemId], section.length ? section : location)
}

export function writeFocusedNames(
  draft: QaTemplateProductDraft,
  focus: QaTemplateProductFocus,
  next: string[],
): QaTemplateProductDraft {
  if (focus.kind === 'location') {
    return { ...draft, locationNames: { ...draft.locationNames, [focus.locationType]: next } }
  }
  if (focus.kind === 'checklist') {
    return { ...draft, sectionNames: { ...draft.sectionNames, [focus.sectionId]: next } }
  }
  return { ...draft, itemNames: { ...draft.itemNames, [focus.itemId]: next } }
}

export function productWritesForInsert(
  placements: QaTemplatePlacement[],
  draft: QaTemplateProductDraft,
  locationType: string | null,
): {
  checklists: { id: string; names: string[] }[]
  items: { id: string; names: string[] }[]
} {
  const locationNames = locationType ? draft.locationNames[locationType] ?? [] : []
  const checklists: { id: string; names: string[] }[] = []
  const items: { id: string; names: string[] }[] = []
  for (const placement of placements) {
    const sectionNames = draft.sectionNames[placement.section_id] ?? []
    const checklistNames = sectionNames.length ? sectionNames : locationNames
    if (checklistNames.length) checklists.push({ id: placement.checklist_id, names: checklistNames })
    for (const item of placement.items) {
      const names = draft.itemNames[item.item_id] ?? []
      if (names.length) items.push({ id: item.instance_id, names })
    }
  }
  return { checklists, items }
}

export function insertedNamesNeedPlacements(
  draft: QaTemplateProductDraft,
  locationType: string | null,
  selectedItemIds: Record<string, Set<string>>,
): boolean {
  if (locationType && (draft.locationNames[locationType] ?? []).length > 0) return true
  for (const [sectionId, items] of Object.entries(selectedItemIds)) {
    if (items.size === 0) continue
    if ((draft.sectionNames[sectionId] ?? []).length > 0) return true
    for (const itemId of items) {
      if ((draft.itemNames[itemId] ?? []).length > 0) return true
    }
  }
  return false
}
